import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiFile, FiPlay, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { useDocuments } from '../hooks/useDocuments';
import { useDocumentAudio } from '../hooks/useDocumentAudio';
import { useAudioPlayer } from '../audio/AudioPlayerContext';
import CreateAudioPanel from '../components/CreateAudioPanel';
import { DocumentItem } from '../types';
import { formatDuration, relativeTime } from '../utils/format';
import AdSlot from '../components/AdSlot';
import '../pages/pages.css';
import './DocumentsPage.css';

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/plain': ['.txt'],
};

export default function DocumentsPage() {
  const { documents, loading, error, uploading, processingIds, upload, remove, isReady } = useDocuments();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Keep a valid selection as the list loads or changes.
  useEffect(() => {
    if (documents.length === 0) {
      setSelectedId(null);
    } else if (selectedId == null || !documents.some((d) => d.documentId === selectedId)) {
      setSelectedId(documents[0].documentId);
    }
  }, [documents, selectedId]);

  const selectedDoc = documents.find((d) => d.documentId === selectedId) ?? null;

  const onDrop = useCallback(
    (accepted: File[]) => {
      accepted.forEach((f) => upload(f).catch(() => undefined));
    },
    [upload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxSize: 15 * 1024 * 1024,
    disabled: uploading,
  });

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Documents</h1>
          <p className="page__subtitle">Select a document to see its audio and create more.</p>
        </div>
      </div>

      {error && <div className="doc-error">{error}</div>}

      <div className="docs-split">
        {/* Left: upload + document list */}
        <div className="docs-list-pane">
          <div {...getRootProps({ className: `dropzone dropzone--compact${isDragActive ? ' dropzone--active' : ''}` })}>
            <input {...getInputProps()} />
            <FiUploadCloud className="dropzone__icon" />
            <div className="dropzone__text">
              <strong>{uploading ? 'Uploading…' : 'Drag & drop a document'}</strong>
              <span className="muted">PDF, Word, PowerPoint or text · up to 15 MB</span>
            </div>
            {uploading && <span className="spinner spinner--sm" />}
          </div>

          {loading ? (
            <div className="loading-block">
              <span className="spinner" />
            </div>
          ) : documents.length === 0 ? (
            <div className="empty">
              <span className="empty__emoji">📄</span>
              <p className="empty__title">No documents yet</p>
              <p className="muted">Upload your first document to get started.</p>
            </div>
          ) : (
            <div className="doc-list">
              {documents.map((doc) => {
                const ready = isReady(doc);
                const processing = processingIds.has(doc.documentId);
                const active = doc.documentId === selectedId;
                return (
                  <button
                    type="button"
                    className={`doc-card doc-card--selectable${active ? ' doc-card--active' : ''}`}
                    key={doc.documentId}
                    onClick={() => setSelectedId(doc.documentId)}
                  >
                    <div className="doc-card__icon">
                      <FiFile />
                    </div>
                    <div className="doc-card__body">
                      <span className="doc-card__name" title={doc.name}>
                        {doc.name}
                      </span>
                      <div className="doc-card__meta">
                        {ready ? (
                          <span className="pill pill--success">Ready</span>
                        ) : processing ? (
                          <span className="pill pill--warning">
                            <span className="spinner spinner--sm" /> Processing
                          </span>
                        ) : (
                          <span className="pill pill--muted">Usable</span>
                        )}
                        <span className="muted">{relativeTime(doc.createdAt)}</span>
                      </div>
                    </div>
                    <span
                      className="btn btn--danger btn--sm doc-card__delete"
                      role="button"
                      tabIndex={0}
                      aria-label={`Delete ${doc.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(doc.documentId);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          remove(doc.documentId);
                        }
                      }}
                    >
                      <FiTrash2 />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: selected document's audio + create widget */}
        <div className="docs-detail-pane">
          {selectedDoc ? (
            <DocumentDetail key={selectedDoc.documentId} doc={selectedDoc} />
          ) : (
            <div className="empty docs-detail-empty">
              <span className="empty__emoji">🎧</span>
              <p className="empty__title">Select a document</p>
              <p className="muted">Pick a document on the left to see its audio and generate new audio.</p>
            </div>
          )}
        </div>
      </div>

      <AdSlot format="banner" label="Sponsored" />
    </div>
  );
}

function DocumentDetail({ doc }: { doc: DocumentItem }) {
  const { items, loading, error, remove } = useDocumentAudio(doc.documentId);
  const player = useAudioPlayer();

  const playItem = (item: (typeof items)[number]) => {
    if (!item.documentId) return;
    player.play({
      id: item.id,
      title: item.title || (item.format === 'audiobook' ? 'Audiobook' : 'Podcast'),
      documentId: item.documentId,
      format: item.format,
      durationSeconds: item.durationSeconds,
    });
  };

  return (
    <>
      <div className="docs-detail-head">
        <h2 className="docs-detail-title" title={doc.name}>
          {doc.name}
        </h2>
      </div>

      <section className="docs-section">
        <h3 className="docs-section__title">Audio</h3>
        {error && <div className="doc-error">{error}</div>}
        {loading ? (
          <div className="loading-block">
            <span className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <p className="muted docs-section__empty">No audio yet. Generate a podcast or audiobook below.</p>
        ) : (
          <div className="lib-list">
            {items.map((item) => {
              const active = player.current?.id === item.id;
              return (
                <div className={`lib-card${active ? ' lib-card--active' : ''}`} key={item.id}>
                  <button className="lib-card__play" onClick={() => playItem(item)} aria-label="Play">
                    <FiPlay />
                  </button>
                  <div className="lib-card__body">
                    <span className="lib-card__title" title={item.title}>
                      {item.title || 'Untitled'}
                    </span>
                    <div className="lib-card__meta">
                      <span className="pill pill--muted">
                        {item.format === 'audiobook' ? '📖 Audiobook' : '🎙️ Podcast'}
                      </span>
                      <span className="muted">{formatDuration(item.durationSeconds)}</span>
                      <span className="muted">{relativeTime(item.createdAt)}</span>
                    </div>
                  </div>
                  <div className="lib-card__actions">
                    <button
                      className="icon-btn icon-btn--danger"
                      onClick={() => {
                        if (window.confirm('Delete this audio?')) remove(item.id);
                      }}
                      aria-label="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="docs-section">
        <h3 className="docs-section__title">Create audio</h3>
        <CreateAudioPanel documentId={doc.documentId} />
      </section>
    </>
  );
}
